import { useState, useEffect, type ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench, Map, Clock, Smartphone, Link2, CheckCircle2,
  ArrowRight, ArrowLeft, Plus, Trash2, Loader2, Sparkles,
} from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext.js';

// ─── Types ────────────────────────────────────────────────────

interface TechDraft {
  name: string;
  email: string;
  phone: string;
  skills: string;
  zone: string;
}

interface WizardState {
  step: number;
  technicians: TechDraft[];
  zones: string[];
  workingHoursStart: string;
  workingHoursEnd: string;
  appointmentSlotMinutes: number;
  travelBufferMinutes: number;
  psaTriggers: string[];
}

const STEPS = [
  { id: 1, label: 'Field team', icon: Wrench },
  { id: 2, label: 'Service area', icon: Map },
  { id: 3, label: 'Scheduling', icon: Clock },
  { id: 4, label: 'Mobile app', icon: Smartphone },
  { id: 5, label: 'PSA integration', icon: Link2 },
  { id: 6, label: 'Done', icon: CheckCircle2 },
] as const;

const INITIAL_STATE: WizardState = {
  step: 1,
  technicians: [{ name: '', email: '', phone: '', skills: '', zone: '' }],
  zones: [''],
  workingHoursStart: '08:00',
  workingHoursEnd: '17:00',
  appointmentSlotMinutes: 60,
  travelBufferMinutes: 30,
  psaTriggers: ['onsite_required', 'hardware_repair', 'installation'],
};

const PSA_TRIGGER_OPTIONS = [
  { id: 'onsite_required', label: 'Ticket tagged "Onsite Required"' },
  { id: 'hardware_repair', label: 'Hardware repair tickets' },
  { id: 'installation',    label: 'Installation tickets' },
  { id: 'network_outage',  label: 'Network outage (high priority)' },
  { id: 'security_camera', label: 'Security / camera system' },
  { id: 'printer_copier',  label: 'Printer / copier' },
];

// ─── Main ─────────────────────────────────────────────────────

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState<WizardState>(INITIAL_STATE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const step = s.step;

  function next() { setS(prev => ({ ...prev, step: Math.min(6, prev.step + 1) })); }
  function back() { setS(prev => ({ ...prev, step: Math.max(1, prev.step - 1) })); }

  async function finish() {
    setSaving(true); setError(null);
    try {
      // 1. Create technicians (skip empty rows)
      for (const t of s.technicians) {
        if (!t.name.trim() || !t.email.trim()) continue;
        await onsiteApiFetch('/api/v1/onsite/technicians', {
          method: 'POST',
          body: JSON.stringify({
            name: t.name.trim(),
            email: t.email.trim(),
            phone: t.phone || undefined,
            skills: t.skills ? t.skills.split(',').map(x => x.trim()).filter(Boolean) : [],
            certifications: [],
            notes: t.zone ? `Zone: ${t.zone}` : undefined,
          }),
        }).catch(() => { /* continue on error */ });
      }

      // 2. Save settings: scheduling + setup_completed
      await onsiteApiFetch('/api/v1/onsite/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          working_hours_start: s.workingHoursStart,
          working_hours_end: s.workingHoursEnd,
          appointment_slot_minutes: s.appointmentSlotMinutes,
          travel_buffer_minutes: s.travelBufferMinutes,
          setup_completed: true,
          setup_completed_at: new Date().toISOString(),
        }),
      });

      navigate('/dashboard');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function skipForNow() {
    setSaving(true);
    try {
      await onsiteApiFetch('/api/v1/onsite/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          setup_completed: true,
          setup_completed_at: new Date().toISOString(),
        }),
      });
      navigate('/dashboard');
    } catch {
      navigate('/dashboard');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Welcome to The One Onsite</h1>
            <p className="text-sm text-muted-foreground">
              {user?.name ? `${user.name}, ` : ''}let&apos;s get your field team ready.
            </p>
          </div>
        </div>

        {/* Stepper */}
        <ol className="mb-8 flex items-center gap-2">
          {STEPS.map((st, i) => {
            const Icon = st.icon;
            const active = step === st.id;
            const done = step > st.id;
            return (
              <li key={st.id} className="flex flex-1 items-center gap-2">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
                  done ? 'border-primary bg-primary/20 text-primary'
                  : active ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-muted-foreground'
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-px flex-1 ${done ? 'bg-primary' : 'bg-border'}`} />
                )}
              </li>
            );
          })}
        </ol>

        <div className="rounded-xl border border-border bg-card p-6">
          {error && (
            <div className="mb-4 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-300">
              {error}
            </div>
          )}

          {step === 1 && <StepTeam s={s} setS={setS} />}
          {step === 2 && <StepZones s={s} setS={setS} />}
          {step === 3 && <StepSchedule s={s} setS={setS} />}
          {step === 4 && <StepMobile />}
          {step === 5 && <StepPsa s={s} setS={setS} />}
          {step === 6 && <StepDone />}

          <div className="mt-8 flex items-center justify-between gap-3 border-t border-border pt-4">
            <button
              onClick={skipForNow}
              disabled={saving}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Skip for now
            </button>
            <div className="flex gap-2">
              {step > 1 && (
                <button
                  onClick={back}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}
              {step < 6 ? (
                <button
                  onClick={next}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={finish}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Finish setup
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Steps ────────────────────────────────────────────────────

function StepHeader({ icon: Icon, title, description }: {
  icon: ElementType; title: string; description: string;
}) {
  return (
    <div className="mb-6 flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function StepTeam({ s, setS }: { s: WizardState; setS: (u: (prev: WizardState) => WizardState) => void }) {
  function update(i: number, patch: Partial<TechDraft>) {
    setS(prev => {
      const next = [...prev.technicians];
      next[i] = { ...next[i], ...patch };
      return { ...prev, technicians: next };
    });
  }
  function addRow() { setS(prev => ({ ...prev, technicians: [...prev.technicians, { name: '', email: '', phone: '', skills: '', zone: '' }] })); }
  function removeRow(i: number) { setS(prev => ({ ...prev, technicians: prev.technicians.filter((_, idx) => idx !== i) })); }

  return (
    <>
      <StepHeader icon={Wrench} title="Add your field technicians"
        description="Add the techs who will receive job assignments. You can always add more later." />
      <div className="space-y-3">
        {s.technicians.map((t, i) => (
          <div key={i} className="rounded-lg border border-border bg-secondary/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Technician {i + 1}</span>
              {s.technicians.length > 1 && (
                <button onClick={() => removeRow(i)} className="text-muted-foreground hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className="input" placeholder="Name" value={t.name}
                onChange={e => update(i, { name: e.target.value })} />
              <input className="input" type="email" placeholder="Email" value={t.email}
                onChange={e => update(i, { email: e.target.value })} />
              <input className="input" placeholder="Phone" value={t.phone}
                onChange={e => update(i, { phone: e.target.value })} />
              <input className="input" placeholder="Territory / zone" value={t.zone}
                onChange={e => update(i, { zone: e.target.value })} />
            </div>
            <input className="input mt-2" placeholder="Skills (comma-separated)" value={t.skills}
              onChange={e => update(i, { skills: e.target.value })} />
          </div>
        ))}
        <button onClick={addRow}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary">
          <Plus className="h-4 w-4" />
          Add another technician
        </button>
      </div>
    </>
  );
}

function StepZones({ s, setS }: { s: WizardState; setS: (u: (prev: WizardState) => WizardState) => void }) {
  function update(i: number, value: string) {
    setS(prev => {
      const next = [...prev.zones];
      next[i] = value;
      return { ...prev, zones: next };
    });
  }
  function addZone() { setS(prev => ({ ...prev, zones: [...prev.zones, ''] })); }
  function removeZone(i: number) { setS(prev => ({ ...prev, zones: prev.zones.filter((_, idx) => idx !== i) })); }

  return (
    <>
      <StepHeader icon={Map} title="Define your service area"
        description="Create zones by city, county, or ZIP-code range. Used to route jobs to the right tech." />
      <div className="space-y-2">
        {s.zones.map((z, i) => (
          <div key={i} className="flex gap-2">
            <input className="input" placeholder={`Zone ${i + 1} (e.g. "Metro East — ZIPs 60601-60612")`}
              value={z} onChange={e => update(i, e.target.value)} />
            {s.zones.length > 1 && (
              <button onClick={() => removeZone(i)} className="rounded-lg border border-border px-3 text-muted-foreground hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        <button onClick={addZone}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary">
          <Plus className="h-4 w-4" />
          Add zone
        </button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Zones are informational for now — you can refine them in Settings anytime.
      </p>
    </>
  );
}

function StepSchedule({ s, setS }: { s: WizardState; setS: (u: (prev: WizardState) => WizardState) => void }) {
  return (
    <>
      <StepHeader icon={Clock} title="Scheduling defaults"
        description="Your team's working hours, appointment slot size, and travel-time buffer." />
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Working hours start</span>
          <input type="time" className="input"
            value={s.workingHoursStart}
            onChange={e => setS(prev => ({ ...prev, workingHoursStart: e.target.value }))} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Working hours end</span>
          <input type="time" className="input"
            value={s.workingHoursEnd}
            onChange={e => setS(prev => ({ ...prev, workingHoursEnd: e.target.value }))} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Appointment slot (minutes)</span>
          <select className="input"
            value={s.appointmentSlotMinutes}
            onChange={e => setS(prev => ({ ...prev, appointmentSlotMinutes: parseInt(e.target.value, 10) }))}>
            {[30, 45, 60, 90, 120].map(v => <option key={v} value={v}>{v} min</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Travel-time buffer (minutes)</span>
          <select className="input"
            value={s.travelBufferMinutes}
            onChange={e => setS(prev => ({ ...prev, travelBufferMinutes: parseInt(e.target.value, 10) }))}>
            {[15, 20, 30, 45, 60].map(v => <option key={v} value={v}>{v} min</option>)}
          </select>
        </label>
      </div>
    </>
  );
}

function StepMobile() {
  return (
    <>
      <StepHeader icon={Smartphone} title="Get the mobile app"
        description="Technicians use The One Onsite mobile app to see jobs, check in, capture photos, and get signatures." />
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-secondary/30 p-4">
          <p className="mb-1 text-sm font-medium text-foreground">iOS</p>
          <p className="mb-3 text-xs text-muted-foreground">Search &quot;The One Onsite&quot; on the App Store.</p>
          <span className="text-xs text-primary">apps.apple.com/app/theone-onsite</span>
        </div>
        <div className="rounded-lg border border-border bg-secondary/30 p-4">
          <p className="mb-1 text-sm font-medium text-foreground">Android</p>
          <p className="mb-3 text-xs text-muted-foreground">Search &quot;The One Onsite&quot; on Google Play.</p>
          <span className="text-xs text-primary">play.google.com/store/apps/theone-onsite</span>
        </div>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Techs sign in with their Hub credentials. No separate password needed.
      </p>
    </>
  );
}

function StepPsa({ s, setS }: { s: WizardState; setS: (u: (prev: WizardState) => WizardState) => void }) {
  function toggle(id: string) {
    setS(prev => ({
      ...prev,
      psaTriggers: prev.psaTriggers.includes(id)
        ? prev.psaTriggers.filter(x => x !== id)
        : [...prev.psaTriggers, id],
    }));
  }

  return (
    <>
      <StepHeader icon={Link2} title="Pick PSA triggers"
        description="Which ticket types automatically create a field dispatch? Pick any that apply." />
      <div className="grid grid-cols-1 gap-2">
        {PSA_TRIGGER_OPTIONS.map(opt => {
          const on = s.psaTriggers.includes(opt.id);
          return (
            <label key={opt.id} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
              on ? 'border-primary bg-primary/10' : 'border-border bg-secondary/30 hover:border-border'
            }`}>
              <input type="checkbox" checked={on} onChange={() => toggle(opt.id)}
                className="h-4 w-4 accent-primary" />
              <span className="text-sm text-foreground">{opt.label}</span>
            </label>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        These are suggestions you can refine later. Actual routing is configured in your PSA.
      </p>
    </>
  );
}

function StepDone() {
  return (
    <>
      <StepHeader icon={CheckCircle2} title="You&apos;re all set"
        description="We'll save your technicians and scheduling preferences, then drop you on the dashboard." />
      <ul className="space-y-2">
        {[
          'Technicians added to your team roster',
          'Working hours and appointment slot defaults saved',
          'PSA trigger preferences noted',
          'Dispatch board and mobile app are ready',
        ].map(item => (
          <li key={item} className="flex items-start gap-2 text-sm text-foreground">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            {item}
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-muted-foreground">
        You can edit any of this in <span className="text-foreground">Settings</span> anytime.
      </p>
    </>
  );
}

// Separate hook for external consumers (e.g. AppShell) to detect whether
// the tenant still needs the welcome wizard.
export function useOnsiteSetupNeeded(): { needed: boolean; loading: boolean } {
  const [state, setState] = useState<{ needed: boolean; loading: boolean }>({ needed: false, loading: true });

  useEffect(() => {
    let cancelled = false;
    onsiteApiFetch<{ setup_completed?: boolean }>('/api/v1/onsite/settings')
      .then(s => { if (!cancelled) setState({ needed: !s.setup_completed, loading: false }); })
      .catch(() => { if (!cancelled) setState({ needed: false, loading: false }); });
    return () => { cancelled = true; };
  }, []);

  return state;
}
